/* eslint-disable max-len */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import {
  CCard, CCardBody, CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
  CPagination, CPaginationItem, CTooltip, CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, 
  CButton, CRow, CCol, CFormInput, CCollapse, CBadge
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilPencil, cilTrash, cilFilter, cilX, cilSearch, cilArrowTop, cilArrowBottom } from '@coreui/icons';
import SearchableSelect from '../SearchableSelect/SearchableSelect';
import { mainColors } from '../../helpers/colors';
import { formatDateOnly } from '../../helpers/dateHelper';
import {
  deleteProductRequest, getProductRequest
} from '../../store/product/productActions';
import { getProductDropdown } from '../../services/endpoints';
import './ProductTable.scss';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import ExportData from '../ExportData/ExportData';

function ProductTable() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getHeaderProps, getResizerFor } = useResizableColumns(['index','WeaveCode','MaterialName','YarnName','ColorName','Certificate','InsertDatetime','actions'], 'ProductTable');

  // Redux state
  const { 
    productList, 
    isLoading, 
    totalCount, 
    totalPages: serverTotalPages
  } = useSelector(state => state.productReducer);

  // Server-side Pagination state
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Server-side Sorting state
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('guid');

  // Filter state
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    WeaveCode: '',
    YarnGuid: '',
    ColorName: '',
    Certificate: ''
  });

  // Dropdown options
  const [yarns, setYarns] = useState([]);

  // Load dropdown options
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const res = await getProductDropdown({ WeaveCode: '' });
        if (res?.data) {
          setYarns(res.data.Yarns || []);
        }
      } catch (err) {
        console.error('Error loading dropdowns:', err);
      }
    };
    loadDropdowns();
  }, []);

  // Load data with filters - server-side
  const loadData = useCallback(() => {
    const trimmedWeaveCode = filters.WeaveCode.trim();
    if (trimmedWeaveCode.length > 0 && trimmedWeaveCode.length < 3) {
      return;
    }

    const payload = {
      WeaveCode: trimmedWeaveCode || null,
      YarnGuid: filters.YarnGuid || null,
      ColorName: filters.ColorName || null,
      Certificate: filters.Certificate || null,
      OrderBy: orderBy,
      IsDescending: order === 'desc',
      PageNumber: page,
      PageSize: rowsPerPage
    };

    dispatch(getProductRequest(payload));
  }, [
    dispatch,
    filters.Certificate,
    filters.ColorName,
    filters.WeaveCode,
    filters.YarnGuid,
    orderBy,
    order,
    page,
    rowsPerPage
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEdit = (item) => {
    navigate(`/products/edit/${item.PrdProductGuid}`, { state: { product: item } });
  };

  const handleDeleteClick = (item) => {
    setDeleteModal({ open: true, item });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.item) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const userId = sessionUserId || userData?.sessionUserId;
      if (userId && userId !== 'undefined') {
        const deleteData = {
          SessionUserId: String(userId),
          ProductId: deleteModal.item.PrdProductGuid
        };
        dispatch(deleteProductRequest(deleteData));
      }
    }
    setDeleteModal({ open: false, item: null });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ open: false, item: null });
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(value);
    setPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      WeaveCode: '',
      YarnGuid: '',
      ColorName: '',
      Certificate: ''
    });
    setPage(1);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleRequestSort = (property) => {
    // Map UI column to backend orderBy field
    const columnToOrderBy = {
      'InsertDatetime': 'insertdatetime',
      'WeaveCode': 'weaveguid',
      'YarnName': 'yarnguid',
      'ColorName': 'colorname',
      'Certificate': 'certificate'
    };
    
    const backendField = columnToOrderBy[property] || 'guid';
    const isAsc = orderBy === backendField && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(backendField);
  };

  const columns = [
    { id: 'index', label: '#', sortable: false },
    { id: 'WeaveCode', label: t('weaveCode'), sortable: true },
    { id: 'MaterialName', label: t('materialName'), sortable: false },
    { id: 'YarnName', label: t('yarnName'), sortable: true },
    { id: 'ColorName', label: t('colorName'), sortable: true },
    { id: 'Certificate', label: t('certificate'), sortable: true },
    { id: 'InsertDatetime', label: t('insertDatetime'), sortable: true },
    { id: 'actions', label: t('actions'), sortable: false }
  ];

  // Export columns
  const exportColumns = useMemo(() => {
    const base = [
      { id: 'WeaveCode', label: t('weaveCode') },
      { id: 'MaterialName', label: t('materialName') },
      { id: 'YarnName', label: t('yarnName') },
      { id: 'ColorName', label: t('colorName') },
      { id: 'Certificate', label: t('certificate') },
      { id: 'InsertDatetime', label: t('insertDatetime') },
    ];

    const extras = [
      { id: 'YarnCode', label: t('yarnCode') },
      { id: 'Lot', label: t('lot') },
      { id: 'Description', label: t('description') },
      { id: 'WeftDensity', label: t('weftDensity') },
      { id: 'WarpDensity', label: t('warpDensity') },
      { id: 'ReedWidth', label: t('reedWidth') },
      { id: 'TotalThreads', label: t('totalThreads') },
      { id: 'ReedNo', label: t('reedNo') },
      { id: 'Dgts', label: t('dgts') },
      { id: 'WeaveType', label: t('weaveType') },
    ];

    const hasAnyValue = (fieldId) => (productList || []).some(it => it && (it[fieldId] !== undefined && it[fieldId] !== null && String(it[fieldId]).trim() !== ''));
    const availableExtras = extras.filter(c => hasAnyValue(c.id));
    return [...base, ...availableExtras];
  }, [t, productList]);

  const yarnOptions = useMemo(() => {
    if (!yarns || !Array.isArray(yarns)) return [];
    return yarns.map((item) => ({
      value: String(item.PrdYarnGuid ?? ''),
      label: item.YarnCode ? `${item.YarnCode} - ${item.YarnName || ''}` : (item.YarnName || '')
    })).filter((item) => item.value);
  }, [yarns]);

  const formatYarnDisplay = (item) => {
    const yarnCode = item?.YarnCode ? String(item.YarnCode).trim() : '';
    const yarnName = item?.YarnName ? String(item.YarnName).trim() : '';
    if (yarnCode && yarnName) return `${yarnCode} - ${yarnName}`;
    return yarnCode || yarnName || '';
  };

  const buildCellValue = (colId, item) => {
    switch (colId) {
      case 'InsertDatetime':
        return formatDateOnly(item[colId]);
      case 'YarnName':
        return formatYarnDisplay(item);
      default:
        return item[colId] ?? '';
    }
  };

  // Calculate pagination using server-side data
  const totalRows = totalCount || 0;
  const totalPages = serverTotalPages || Math.ceil(totalRows / rowsPerPage) || 1;
  const paginationItems = [];
  const maxVisiblePages = 7;
  let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  if (startPage > 1) {
    paginationItems.push(
      <CPaginationItem key="first" onClick={() => handleChangePage(1)}>
        1
      </CPaginationItem>
    );
    if (startPage > 2) {
      paginationItems.push(
        <CPaginationItem key="start-ellipsis" disabled>
          ...
        </CPaginationItem>
      );
    }
  }

  for (let i = startPage; i <= endPage; i++) {
    paginationItems.push(
      <CPaginationItem key={i} active={i === page} onClick={() => handleChangePage(i)}>
        {i}
      </CPaginationItem>
    );
  }

  if (endPage < totalPages) {
    if (endPage < totalPages - 1) {
      paginationItems.push(
        <CPaginationItem key="end-ellipsis" disabled>
          ...
        </CPaginationItem>
      );
    }
    paginationItems.push(
      <CPaginationItem key="last" onClick={() => handleChangePage(totalPages)}>
        {totalPages}
      </CPaginationItem>
    );
  }

  const activeFilterCount = Object.values(filters).filter(value => value && value.toString().trim() !== '').length;
  const isDeleteIMaterial = deleteModal.item?.MaterialName
    ? String(deleteModal.item.MaterialName).trim().toLocaleLowerCase('tr').startsWith('i')
    : false;

  // Get current orderBy column for UI indicator
  const getOrderByColumn = () => {
    const orderByToColumn = {
      'insertdatetime': 'InsertDatetime',
      'weaveguid': 'WeaveCode',
      'yarnguid': 'YarnName',
      'colorname': 'ColorName',
      'certificate': 'Certificate'
    };
    return orderByToColumn[orderBy] || '';
  };

  const currentOrderByColumn = getOrderByColumn();

  return (
    <div className="product-table-container">
      <CCard>
        <CCardBody>
          <div className="filter-controls mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <CButton
                  color={showFilters ? "primary" : "outline-primary"}
                  variant={showFilters ? "solid" : "outline"}
                  size="sm"
                  onClick={toggleFilters}
                  className="filter-toggle-btn"
                >
                  <CIcon icon={cilFilter} size="sm" className="me-1" />
                  {t('filters')}
                  {activeFilterCount > 0 && (
                    <span className="filter-count-badge ms-1">{activeFilterCount}</span>
                  )}
                </CButton>
                {activeFilterCount > 0 && (
                  <CButton
                    color="outline-secondary"
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    className="clear-filters-btn"
                  >
                    <CIcon icon={cilX} size="sm" className="me-1" />
                    {t('clearFilters')}
                  </CButton>
                )}
              </div>
              <div className="results-info d-flex align-items-center gap-2">
                <span className="text-muted">
                  {totalRows} {t('resultsFound')}
                </span>
                <ExportData
                  columns={exportColumns}
                  rows={productList}
                  allRows={productList}
                  buildCellValue={buildCellValue}
                  filenamePrefix="products_export"
                  triggerDisabled={!productList || productList.length === 0}
                />
              </div>
            </div>
          </div>

          {/* Filtre Alanları */}
          <CCollapse visible={showFilters}>
            <div className="filter-panel mb-4">
              <CRow className="g-3 justify-content-center align-items-end filter-grid">
                {/* Weave Code Text Input */}
                <CCol xs={12} sm={6} lg={3} className="filter-grid-col">
                  <div className="filter-field">
                    <label className="filter-label">{t('weaveCode')}</label>
                    <div className="filter-input-wrapper">
                      <CIcon icon={cilSearch} className="filter-input-icon" />
                      <CFormInput
                        type="text"
                        placeholder={`${t('search')}...`}
                        value={filters.WeaveCode}
                        onChange={(e) => handleFilterChange('WeaveCode', e.target.value)}
                        className="filter-input"
                        size="sm"
                      />
                      {filters.WeaveCode && (
                        <CButton
                          color="link"
                          size="sm"
                          className="filter-clear-btn"
                          onClick={() => handleFilterChange('WeaveCode', '')}
                        >
                          <CIcon icon={cilX} size="sm" />
                        </CButton>
                      )}
                    </div>
                  </div>
                </CCol>

                {/* Yarn Dropdown with Search */}
                <CCol xs={12} sm={6} lg={3} className="filter-grid-col">
                  <div className="filter-field">
                    <label className="filter-label">{t('yarnName')}</label>
                    <SearchableSelect
                      id="FilterYarnGuid"
                      name="FilterYarnGuid"
                      options={yarnOptions}
                      value={filters.YarnGuid}
                      onChange={(selectedValue) => handleFilterChange('YarnGuid', selectedValue)}
                      placeholder={t('all')}
                      displayKey="label"
                      valueKey="value"
                      emptyText={t('noResultsFound')}
                      className="filter-searchable-select"
                      size="sm"
                    />
                  </div>
                </CCol>

                {/* Color Name Text Input */}
                <CCol xs={12} sm={6} lg={3} className="filter-grid-col">
                  <div className="filter-field">
                    <label className="filter-label">{t('colorName')}</label>
                    <div className="filter-input-wrapper">
                      <CIcon icon={cilSearch} className="filter-input-icon" />
                      <CFormInput
                        type="text"
                        placeholder={`${t('search')}...`}
                        value={filters.ColorName}
                        onChange={(e) => handleFilterChange('ColorName', e.target.value)}
                        className="filter-input"
                        size="sm"
                      />
                      {filters.ColorName && (
                        <CButton
                          color="link"
                          size="sm"
                          className="filter-clear-btn"
                          onClick={() => handleFilterChange('ColorName', '')}
                        >
                          <CIcon icon={cilX} size="sm" />
                        </CButton>
                      )}
                    </div>
                  </div>
                </CCol>

                {/* Certificate Text Input */}
                <CCol xs={12} sm={6} lg={3} className="filter-grid-col">
                  <div className="filter-field">
                    <label className="filter-label">{t('certificate')}</label>
                    <div className="filter-input-wrapper">
                      <CIcon icon={cilSearch} className="filter-input-icon" />
                      <CFormInput
                        type="text"
                        placeholder={`${t('search')}...`}
                        value={filters.Certificate}
                        onChange={(e) => handleFilterChange('Certificate', e.target.value)}
                        className="filter-input"
                        size="sm"
                      />
                      {filters.Certificate && (
                        <CButton
                          color="link"
                          size="sm"
                          className="filter-clear-btn"
                          onClick={() => handleFilterChange('Certificate', '')}
                        >
                          <CIcon icon={cilX} size="sm" />
                        </CButton>
                      )}
                    </div>
                  </div>
                </CCol>
              </CRow>
            </div>
          </CCollapse>

          <CTable hover responsive>
            <CTableHead>
              <CTableRow>
                {columns.map((column) => {
                  const headerProps = getHeaderProps(column.id);
                  return (
                    <CTableHeaderCell 
                      key={column.id} 
                      className="table-header-cell" 
                      style={{ cursor: column.sortable ? 'pointer' : 'default', paddingRight: '8px', ...(headerProps.style || {}) }} 
                      onClick={() => column.sortable && handleRequestSort(column.id)}
                    >
                      {column.label}
                      {column.sortable && currentOrderByColumn === column.id && (
                        <CIcon icon={order === 'asc' ? cilArrowTop : cilArrowBottom} size="sm" className="ms-1" />
                      )}
                      {getResizerFor(column.id)}
                    </CTableHeaderCell>
                  );
                })}
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {productList && productList.length > 0 ? (
                productList.map((item, index) => (
                  <CTableRow key={item.PrdProductGuid || index}>
                    <CTableDataCell>{(page - 1) * rowsPerPage + index + 1}</CTableDataCell>
                    <CTableDataCell>
                      <CBadge color="primary" shape="rounded-pill">
                        {item.WeaveCode || '-'}
                      </CBadge>
                    </CTableDataCell>
                    <CTableDataCell>{item.MaterialName}</CTableDataCell>
                    <CTableDataCell>
                      <CBadge color="info" shape="rounded-pill">
                        {formatYarnDisplay(item) || '-'}
                      </CBadge>
                    </CTableDataCell>
                    <CTableDataCell>{item.ColorName}</CTableDataCell>
                    <CTableDataCell>{item.Certificate}</CTableDataCell>
                    <CTableDataCell>{formatDateOnly(item.InsertDatetime)}</CTableDataCell>
                    <CTableDataCell>
                      <div className="d-flex gap-2">
                        <CTooltip content={t('editProduct')}>
                          <div className="cursor-pointer p-1" onClick={() => handleEdit(item)}>
                            <CIcon icon={cilPencil} size="sm" style={{ color: mainColors.primary }} />
                          </div>
                        </CTooltip>
                        <CTooltip content={t('deleteProduct')}>
                          <div className="cursor-pointer p-1" onClick={() => handleDeleteClick(item)}>
                            <CIcon icon={cilTrash} size="sm" style={{ color: '#dc3545' }} />
                          </div>
                        </CTooltip>
                      </div>
                    </CTableDataCell>
                  </CTableRow>
                ))
              ) : (
                <CTableRow>
                  <CTableDataCell colSpan={columns.length} className="text-center py-4">
                    <div className="no-data-message">
                      {isLoading ? (
                        <div className="loading-message">
                          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                          {t('loadingData')}
                        </div>
                      ) : activeFilterCount > 0 ? (
                        <div className="no-results-message">
                          <CIcon icon={cilSearch} size="xl" className="mb-2 text-muted" />
                          <div>{t('noResultsFound')}</div>
                          <CButton
                            color="link"
                            size="sm"
                            onClick={clearFilters}
                            className="mt-2"
                          >
                            {t('clearFiltersToSeeAll')}
                          </CButton>
                        </div>
                      ) : (
                        <div className="text-muted">{t('noDataFound')}</div>
                      )}
                    </div>
                  </CTableDataCell>
                </CTableRow>
              )}
            </CTableBody>
          </CTable>
          {totalRows > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">{t('show')}:</span>
                <select className="form-select form-select-sm" value={rowsPerPage} onChange={(e) => handleChangeRowsPerPage(parseInt(e.target.value, 10))} style={{ width: 'auto' }}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-muted">{t('entries')}</span>
              </div>
              <CPagination className="mb-0">
                <CPaginationItem 
                  aria-label={t('previous')}
                  onClick={() => page > 1 && handleChangePage(page - 1)} 
                  disabled={page === 1}
                >
                  &laquo;
                </CPaginationItem>
                {paginationItems}
                <CPaginationItem 
                  aria-label={t('next')}
                  onClick={() => page < totalPages && handleChangePage(page + 1)} 
                  disabled={page === totalPages}
                >
                  &raquo;
                </CPaginationItem>
              </CPagination>
              <div className="text-muted">
                {t('showing')} {Math.min((page - 1) * rowsPerPage + 1, totalRows)} {t('to')}{' '}
                {Math.min(page * rowsPerPage, totalRows)} {t('of')} {totalRows} {t('entries')}
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>
      <CModal visible={deleteModal.open} onClose={handleDeleteCancel}>
        <CModalHeader>
          <CModalTitle>{t('deleteProduct')}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p>{t('confirmDeleteProduct')}</p>
          {deleteModal.item && (
            <div className="bg-light p-3 rounded">
              {isDeleteIMaterial ? (
                <>
                  <strong>{t('materialName')}:</strong> {deleteModal.item.MaterialName}<br/>
                  <strong>{t('insertDatetime')}:</strong> {formatDateOnly(deleteModal.item.InsertDatetime)}
                </>
              ) : (
                <>
                  <strong>{t('weaveCode')}:</strong> {deleteModal.item.WeaveCode}<br/>
                  <strong>{t('materialName')}:</strong> {deleteModal.item.MaterialName}<br/>
                  <strong>{t('insertDatetime')}:</strong> {formatDateOnly(deleteModal.item.InsertDatetime)}
                </>
              )}
            </div>
          )}
          <p className="text-danger mt-2 mb-0">
            <small>{t('productDeleteWarning')}</small>
          </p>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={handleDeleteCancel}>{t('cancel')}</CButton>
          <CButton color="danger" onClick={handleDeleteConfirm}>{t('delete')}</CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
}

export default ProductTable;
